import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
  Typography,
  Row,
  Col,
  Radio,
  Progress,
  Checkbox,
  Drawer,
  Descriptions,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DownloadOutlined,
  UploadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const { Text } = Typography;
const { TextArea } = Input;

const BASE_URL = 'https://ds2.cn/mp';
const API = '/api/v1/meme-plant';

interface PlantType {
  id: number;
  code: string;
  plantId?: string;
  name: string;
  description: string;
  emoji?: string;
  level: string;
  labelZh?: string;
  category?: string;
  climateCategory?: string;
  lifeIndex?: number;
  dailyProductionMin?: number;
  dailyProductionMax?: number;
  texture?: string;
  effects?: string;
  created: string;
  updated: string;
  instanceCount?: number;
}

const RARITY_OPTIONS = ['UR', 'QR', 'SSR', 'SR'];

const PLANT_TEMPLATE_HEADERS = [
  '编号(card_id)', 'plant_id', '名称', 'emoji', '标签',
  '类别(category)', '气候分类', '稀有度',
  '生命指数(life_index)', '日产量下限', '日产量上限',
  '图片(texture)', '效果JSON(effects)', '描述',
];

function rowToPlantDto(row: any) {
  const get = (...keys: string[]) => {
    for (const k of keys) if (row[k] !== undefined && row[k] !== '') return row[k];
    return undefined;
  };
  const tryJson = (v: any) => {
    if (v === undefined || v === '' || v === null) return undefined;
    if (typeof v === 'object') return v;
    try { return JSON.parse(String(v)); } catch { return String(v); }
  };
  const toNum = (v: any) => (v === '' || v === undefined || v === null ? undefined : Number(v));

  // code 优先用 card_id；没有则用 plant_id
  const cardId = get('编号(card_id)', 'code');
  const plantId = get('plant_id');

  return {
    code: (cardId || plantId || '').toString().trim(),
    plantId,
    name: get('名称', 'name') || '',
    description: get('描述', 'description') || '',
    emoji: get('emoji'),
    labelZh: get('标签', 'labelZh'),
    category: get('类别(category)', 'category'),
    climateCategory: get('气候分类', 'climateCategory'),
    level: get('稀有度', 'level') || 'SR',
    lifeIndex: toNum(get('生命指数(life_index)', 'lifeIndex')),
    dailyProductionMin: toNum(get('日产量下限', 'dailyProductionMin')),
    dailyProductionMax: toNum(get('日产量上限', 'dailyProductionMax')),
    texture: get('图片(texture)', 'texture'),
    effects: tryJson(get('效果JSON(effects)', 'effects')),
  };
}

const PlantTab: React.FC = () => {
  const [types, setTypes] = useState<PlantType[]>([]);
  const [loading, setLoading] = useState(false);
  const [typeModalVisible, setTypeModalVisible] = useState(false);
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [batchGenerateModalVisible, setBatchGenerateModalVisible] = useState(false);
  const [batchUploadModalVisible, setBatchUploadModalVisible] = useState(false);
  const [selectedType, setSelectedType] = useState<PlantType | null>(null);
  const [uploadedData, setUploadedData] = useState<any[]>([]);
  const [overwriteOnImport, setOverwriteOnImport] = useState<boolean>(false);

  // 详情抽屉
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailRecord, setDetailRecord] = useState<PlantType | null>(null);

  const [batchProgress, setBatchProgress] = useState({
    currentType: '',
    currentTypeIndex: 0,
    totalTypes: 0,
    status: 'idle' as 'idle' | 'running' | 'completed' | 'error',
    completedTypes: [] as string[],
    failedTypes: [] as { code: string; error: string }[],
  });

  const [typeForm] = Form.useForm();
  const [generateForm] = Form.useForm();
  const [batchGenerateForm] = Form.useForm();

  useEffect(() => { fetchTypes(); }, []);

  const fetchTypes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/types`);
      const data = await res.json();
      if (data.success) setTypes(data.data || []);
      else message.error(data.message || '获取Meme 植物种类失败');
    } catch { message.error('获取Meme 植物种类失败'); }
    finally { setLoading(false); }
  };

  const createType = async (values: any) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: values.code, name: values.name, description: values.description,
          emoji: values.emoji || null, level: values.level || 'SR',
          labelZh: values.labelZh || '植物', category: values.category || 'plant',
          climateCategory: values.climateCategory || 'plant',
          lifeIndex: values.lifeIndex ?? null,
          dailyProductionMin: values.dailyProductionMin ?? 10,
          dailyProductionMax: values.dailyProductionMax ?? 30,
          texture: values.texture || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        message.success('创建成功');
        setTypeModalVisible(false);
        typeForm.resetFields();
        fetchTypes();
      } else { message.error(data.message || '创建失败'); }
    } catch { message.error('创建失败'); }
    finally { setLoading(false); }
  };

  const downloadTemplate = () => {
    const sample = [{
      [PLANT_TEMPLATE_HEADERS[0]]: 'BC211-004',
      [PLANT_TEMPLATE_HEADERS[1]]: 'ginkgo_001',
      [PLANT_TEMPLATE_HEADERS[2]]: '古银杏',
      [PLANT_TEMPLATE_HEADERS[3]]: '🍑',
      [PLANT_TEMPLATE_HEADERS[4]]: '植物',
      [PLANT_TEMPLATE_HEADERS[5]]: 'plant',
      [PLANT_TEMPLATE_HEADERS[6]]: 'plant',
      [PLANT_TEMPLATE_HEADERS[7]]: 'UR',
      [PLANT_TEMPLATE_HEADERS[8]]: 51,
      [PLANT_TEMPLATE_HEADERS[9]]: 10,
      [PLANT_TEMPLATE_HEADERS[10]]: 30,
      [PLANT_TEMPLATE_HEADERS[11]]: '/static/textures/plants/004.png',
      [PLANT_TEMPLATE_HEADERS[12]]: '[{"type":"heal","value":22},{"type":"spirit_boost","value":45}]',
      [PLANT_TEMPLATE_HEADERS[13]]: '亿万年前的银杏祖先，结出金黄色肉质种实...',
    }];
    const ws = XLSX.utils.json_to_sheet(sample, { header: PLANT_TEMPLATE_HEADERS });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Meme 植物');
    XLSX.writeFile(wb, 'Meme 植物-种类导入模板.xlsx');
    message.success('模板下载成功');
  };

  const handleFileUpload = (file: File) => {
    const isJson = file.name.toLowerCase().endsWith('.json');
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let rows: any[] = [];
        if (isJson) {
          rows = JSON.parse(e.target?.result as string);
        } else {
          const wb = XLSX.read(e.target?.result, { type: 'binary' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          rows = XLSX.utils.sheet_to_json(ws);
          const suspicious = rows.some((r: any) =>
            typeof r.emoji === 'string' && r.emoji.length === 1 &&
            r.emoji.charCodeAt(0) >= 0xF000 && r.emoji.charCodeAt(0) <= 0xFFFD,
          );
          if (suspicious) {
            message.warning(
              '检测到 xlsx 里 emoji 字段疑似被截断（SheetJS 已知 bug）。' +
              '请改用同目录下的 .json 文件上传以避免乱码。',
              8,
            );
          }
        }
        const valid = rows.map((r: any) => rowToPlantDto(r)).filter(d => d.code && d.name);
        setUploadedData(valid);
        message.success(`成功读取 ${valid.length} 条数据${isJson ? '（JSON）' : '（XLSX）'}`);
      } catch (err) { message.error('文件解析失败'); console.error(err); }
    };
    if (isJson) reader.readAsText(file, 'utf-8');
    else reader.readAsBinaryString(file);
    return false;
  };

  const batchCreateTypes = async () => {
    if (uploadedData.length === 0) { message.warning('请先上传文件'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/types/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: uploadedData, overwrite: overwriteOnImport }),
      });
      const data = await res.json();
      if (data.success) {
        const r = data.data || {};
        const failedList = (r.failed || []).slice(0, 8).map((f: any) => `${f.code}: ${f.error}`).join('\n');
        message.success(
          `批量导入完成！创建 ${r.created}，更新 ${r.updated}，跳过 ${r.skipped}，失败 ${r.failed?.length || 0}`,
          5,
        );
        if (failedList) {
          Modal.warning({
            title: '部分条目失败',
            content: <pre style={{ maxHeight: 300, overflow: 'auto' }}>{failedList}</pre>,
            width: 600,
          });
        }
      } else {
        message.error(data.message || '批量导入失败');
      }
    } catch (err) {
      console.error(err);
      message.error('批量导入失败');
    } finally {
      setLoading(false);
      setBatchUploadModalVisible(false);
      setUploadedData([]);
      fetchTypes();
    }
  };

  const generateInstances = async (values: any) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/types/${selectedType?.id}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: values.quantity }),
      });
      const data = await res.json();
      if (data.success) {
        message.success(`成功生成 ${values.quantity} 个实例`);
        const instances = data.data.instances as { hash: string }[];
        const content = instances.map(i => `${BASE_URL}/${i.hash}`).join('\r\n') + '\r\n';
        const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([BOM, content], { type: 'text/plain;charset=utf-8' });
        saveAs(blob, `${selectedType?.code}.txt`);
        setGenerateModalVisible(false);
        generateForm.resetFields();
        fetchTypes();
      } else { message.error(data.message || '生成失败'); }
    } catch { message.error('生成失败'); }
    finally { setLoading(false); }
  };

  const fetchWithRetry = async (url: string, options: RequestInit, maxRetries = 3, retryDelay = 1000): Promise<Response> => {
    let lastError: Error | null = null;
    for (let i = 0; i <= maxRetries; i++) {
      try {
        const res = await fetch(url, options);
        if (res.ok) return res;
        if (res.status >= 400 && res.status < 500) return res;
        throw new Error(`Server error: ${res.status}`);
      } catch (err: any) {
        lastError = err;
        if (i === maxRetries) break;
        await new Promise(r => setTimeout(r, retryDelay * Math.pow(2, i)));
      }
    }
    throw lastError || new Error('请求失败');
  };

  const downloadAsZip = async (files: { code: string; content: string }[]) => {
    try {
      setLoading(true);
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      files.forEach(f => zip.file(`${f.code}.txt`, `﻿${f.content}`, { binary: false }));
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `meme_plant_batch_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.zip`);
      message.success(`成功生成ZIP文件包含 ${files.length} 个文件`);
    } catch (err) { message.error('生成ZIP文件失败'); console.error(err); }
    finally { setLoading(false); }
  };

  const batchGenerateInstances = async (values: any) => {
    const { quantity, downloadType } = values;
    setBatchProgress({ currentType: '', currentTypeIndex: 0, totalTypes: types.length, status: 'running', completedTypes: [], failedTypes: [] });
    setLoading(true);
    try {
      const allFiles: { code: string; content: string }[] = [];
      for (let i = 0; i < types.length; i++) {
        const t = types[i];
        setBatchProgress(p => ({ ...p, currentType: t.code, currentTypeIndex: i + 1 }));
        try {
          const res = await fetchWithRetry(`${API}/types/${t.id}/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ quantity }),
          }, 3, 1000);
          const data = await res.json();
          if (data.success && data.data?.instances) {
            const content = data.data.instances.map((i: any) => `${BASE_URL}/${i.hash}`).join('\r\n') + '\r\n';
            allFiles.push({ code: t.code, content });
            setBatchProgress(p => ({ ...p, completedTypes: [...p.completedTypes, t.code] }));
            if (downloadType === 'batch') {
              const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
              saveAs(new Blob([BOM, content], { type: 'text/plain;charset=utf-8' }), `${t.code}.txt`);
              await new Promise(r => setTimeout(r, 500));
            }
          } else { throw new Error(data.message || '生成失败'); }
        } catch (err: any) {
          setBatchProgress(p => ({ ...p, failedTypes: [...p.failedTypes, { code: t.code, error: err.message || '未知错误' }] }));
        }
      }
      if (downloadType === 'zip' && allFiles.length > 0) await downloadAsZip(allFiles);
      setBatchProgress(p => ({ ...p, status: 'completed' }));
      const successCount = allFiles.length;
      message.success(`批量生成完成：成功 ${successCount} 个`);
      fetchTypes();
    } catch (err) { message.error('批量生成失败'); setBatchProgress(p => ({ ...p, status: 'error' })); }
    finally { setLoading(false); }
  };

  const renderLevel = (v: string) => {
    const colors: Record<string, string> = { UR: 'red', SSR: 'gold', SR: 'purple', QR: 'default' };
    return <Tag color={colors[v] || 'default'}>{v}</Tag>;
  };

  const fmtNum = (v?: number) => (v === null || v === undefined ? '—' : v);

  const renderJson = (s?: string | null) => {
    if (!s) return <Text type="secondary">—</Text>;
    try {
      const obj = JSON.parse(s);
      return <pre style={{ margin: 0, fontSize: 12, maxHeight: 200, overflow: 'auto' }}>{JSON.stringify(obj, null, 2)}</pre>;
    } catch {
      return <pre style={{ margin: 0, fontSize: 12 }}>{s}</pre>;
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60, fixed: 'left' as const },
    { title: '编号', dataIndex: 'code', key: 'code', width: 130, fixed: 'left' as const },
    { title: 'plant_id', dataIndex: 'plantId', key: 'plantId', width: 160 },
    { title: '名称', dataIndex: 'name', key: 'name', width: 110, fixed: 'left' as const },
    { title: 'emoji', dataIndex: 'emoji', key: 'emoji', width: 60 },
    {
      title: '稀有度', dataIndex: 'level', key: 'level', width: 80,
      render: renderLevel,
    },
    { title: '标签', dataIndex: 'labelZh', key: 'labelZh', width: 80 },
    { title: '类别', dataIndex: 'category', key: 'category', width: 80 },
    { title: '气候', dataIndex: 'climateCategory', key: 'climateCategory', width: 80 },
    {
      title: '日产范围', key: 'production', width: 110,
      render: (_: any, r: PlantType) => `${r.dailyProductionMin ?? '?'} – ${r.dailyProductionMax ?? '?'}`,
    },
    { title: '生命指数', dataIndex: 'lifeIndex', key: 'lifeIndex', width: 80, render: fmtNum },
    { title: '图片', dataIndex: 'texture', key: 'texture', width: 200, ellipsis: true },
    {
      title: 'effects', dataIndex: 'effects', key: 'effects', width: 200, ellipsis: true,
      render: (v?: string) => v ? <Text type="secondary" style={{ fontSize: 11 }}>{v.length > 80 ? v.slice(0, 80) + '…' : v}</Text> : '—',
    },
    {
      title: '实例数', dataIndex: 'instanceCount', key: 'instanceCount', width: 80,
      render: (n: number) => n || 0,
    },
    {
      title: '操作', key: 'action', width: 150, fixed: 'right' as const,
      render: (_: any, record: PlantType) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />}
            onClick={() => { setDetailRecord(record); setDetailVisible(true); }}>
            详情
          </Button>
          <Button type="link" size="small"
            onClick={() => { setSelectedType(record); setGenerateModalVisible(true); }}>
            生成实例
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card
      bordered={false}
      extra={
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setTypeModalVisible(true)}>创建新种类</Button>
          <Button icon={<UploadOutlined />} onClick={() => setBatchUploadModalVisible(true)}>批量导入</Button>
          <Button onClick={() => setBatchGenerateModalVisible(true)}>批量生成</Button>
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>下载模板</Button>
          <Button icon={<ReloadOutlined />} onClick={fetchTypes}>刷新</Button>
        </Space>
      }
    >
      <Table columns={columns} dataSource={types} rowKey="id" loading={loading} pagination={{ pageSize: 10 }} scroll={{ x: 1800 }} />

      <Modal title="创建Meme 植物种类" open={typeModalVisible} onOk={() => typeForm.submit()}
        onCancel={() => { setTypeModalVisible(false); typeForm.resetFields(); }} width={700}>
        <Form form={typeForm} layout="vertical" onFinish={createType}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="code" label="编号(card_id)" rules={[{ required: true }]}><Input placeholder="BC211-004" /></Form.Item></Col>
            <Col span={12}><Form.Item name="name" label="名称" rules={[{ required: true }]}><Input placeholder="古银杏" /></Form.Item></Col>
          </Row>
          <Form.Item name="description" label="描述" rules={[{ required: true }]}>
            <TextArea rows={3} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="level" label="稀有度" initialValue="SR" rules={[{ required: true }]}>
              <Select>{RARITY_OPTIONS.map(r => <Select.Option key={r} value={r}>{r}</Select.Option>)}</Select>
            </Form.Item></Col>
            <Col span={8}><Form.Item name="emoji" label="emoji"><Input placeholder="🍑" /></Form.Item></Col>
            <Col span={8}><Form.Item name="lifeIndex" label="生命指数"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="dailyProductionMin" label="日产量下限" initialValue={10}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="dailyProductionMax" label="日产量上限" initialValue={30}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item name="texture" label="图片(texture)"><Input placeholder="/static/textures/plants/004.png" /></Form.Item>
          <Text type="secondary">effects（治疗/饱食加成等数组）建议通过 Excel 批量导入</Text>
        </Form>
      </Modal>

      <Modal title={`生成实例 - ${selectedType?.name}`} open={generateModalVisible}
        onOk={() => generateForm.submit()}
        onCancel={() => { setGenerateModalVisible(false); generateForm.resetFields(); }}>
        <Form form={generateForm} layout="vertical" onFinish={generateInstances}>
          <Form.Item name="quantity" label="生成数量" initialValue={100} rules={[{ required: true }]}>
            <InputNumber min={1} max={10000} style={{ width: '100%' }} />
          </Form.Item>
          <Text type="secondary">生成后下载 txt（{BASE_URL}/&lt;hash&gt;）</Text>
        </Form>
      </Modal>

      <Modal title="批量导入Meme 植物种类" open={batchUploadModalVisible}
        onOk={batchCreateTypes} okText={overwriteOnImport ? '确认导入（覆盖已存在）' : '确认导入'} cancelText="取消"
        onCancel={() => { setBatchUploadModalVisible(false); setUploadedData([]); }} width={900}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text>请上传 Excel 文件（与「下载模板」按钮一致）。</Text>
            <br />
            <Text type="secondary">推荐使用本仓库的 <code>Ancient Beast/imports/Meme 植物-种类导入.xlsx</code></Text>
          </div>
          <Checkbox
            checked={overwriteOnImport}
            onChange={e => setOverwriteOnImport(e.target.checked)}
          >
            <Text strong>覆盖已存在的条目（按编号 code 匹配，UPDATE 已有的）</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              不勾选时遇到重复编号会跳过；勾选则用新数据 UPDATE 已存在的行
            </Text>
          </Checkbox>
          <input type="file" accept=".xlsx,.xls,.json"
            onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); }}
            style={{ padding: 8, border: '1px dashed #d9d9d9', borderRadius: 4, width: '100%', cursor: 'pointer' }} />
          <Text type="warning" style={{ fontSize: 12 }}>
            ⚠️ 上传含 emoji 的数据时，推荐使用 <code>.json</code> 文件（xlsx 解析库对 emoji 有截断 bug）。
            两个文件都在 <code>Ancient Beast/imports/</code> 下。
          </Text>
          {uploadedData.length > 0 && (
            <div>
              <Text strong>已读取 {uploadedData.length} 条数据：</Text>
              <Table dataSource={uploadedData} size="small" pagination={false} scroll={{ y: 300 }}
                rowKey={(_, i) => String(i || 0)}
                columns={[
                  { title: '编号', dataIndex: 'code', width: 110 },
                  { title: 'plant_id', dataIndex: 'plantId', width: 150 },
                  { title: '名称', dataIndex: 'name', width: 110 },
                  { title: '稀有度', dataIndex: 'level', width: 70 },
                  { title: '生命', dataIndex: 'lifeIndex', width: 60 },
                  { title: '产能下限', dataIndex: 'dailyProductionMin', width: 80 },
                  { title: '产能上限', dataIndex: 'dailyProductionMax', width: 80 },
                ]} />
            </div>
          )}
        </Space>
      </Modal>

      <Modal title="批量生成Meme 植物实例" open={batchGenerateModalVisible} footer={null} width={600}
        onCancel={() => { setBatchGenerateModalVisible(false); batchGenerateForm.resetFields(); }}>
        <Form form={batchGenerateForm} layout="vertical" onFinish={batchGenerateInstances}>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">将为所有植物种类（共 {types.length} 个）各生成指定数量的实例</Text>
          </div>
          <Form.Item name="quantity" label="每种生成数量" rules={[{ required: true }]}>
            <InputNumber min={1} max={10000} style={{ width: '100%' }} disabled={loading} />
          </Form.Item>
          <Form.Item name="downloadType" label="下载方式" initialValue="batch" rules={[{ required: true }]}>
            <Radio.Group disabled={loading}>
              <Radio value="batch">即时下载</Radio>
              <Radio value="zip">ZIP 打包</Radio>
            </Radio.Group>
          </Form.Item>
          {batchProgress.status !== 'idle' && (
            <div style={{ marginBottom: 16, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
              <Text strong>生成进度</Text>
              <div style={{ marginBottom: 8 }}>
                {batchProgress.status === 'running' && <Text>正在处理: {batchProgress.currentType}</Text>}
                {batchProgress.status === 'completed' && <Text>✓ 完成</Text>}
                {batchProgress.status === 'error' && <Text type="danger">✗ 出错</Text>}
              </div>
              <Progress percent={batchProgress.totalTypes > 0 ? Math.round((batchProgress.currentTypeIndex / batchProgress.totalTypes) * 100) : 0}
                status={batchProgress.status === 'completed' ? 'success' : batchProgress.status === 'error' ? 'exception' : 'active'}
                format={() => `${batchProgress.currentTypeIndex}/${batchProgress.totalTypes}`} />
              <div style={{ marginTop: 12, fontSize: 12 }}>
                <Text type="success">已完成: {batchProgress.completedTypes.length} 个</Text>
                {batchProgress.failedTypes.length > 0 && (
                  <><br /><Text type="danger">失败: {batchProgress.failedTypes.length} 个</Text></>
                )}
              </div>
            </div>
          )}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading} disabled={batchProgress.status === 'running'} icon={<DownloadOutlined />}>
                {loading ? '生成中...' : '开始批量生成'}
              </Button>
              <Button onClick={() => { setBatchGenerateModalVisible(false); batchGenerateForm.resetFields(); setBatchProgress(p => ({ ...p, status: 'idle' })); }}>关闭</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情抽屉 */}
      <Drawer
        title={detailRecord ? `${detailRecord.emoji || ''} ${detailRecord.name} · ${detailRecord.code}` : '详情'}
        open={detailVisible}
        width={680}
        onClose={() => setDetailVisible(false)}
      >
        {detailRecord && (
          <Descriptions column={2} size="small" bordered labelStyle={{ width: 110 }}>
            <Descriptions.Item label="ID">{detailRecord.id}</Descriptions.Item>
            <Descriptions.Item label="编号">{detailRecord.code}</Descriptions.Item>
            <Descriptions.Item label="plant_id">{detailRecord.plantId || '—'}</Descriptions.Item>
            <Descriptions.Item label="名称">{detailRecord.name}</Descriptions.Item>
            <Descriptions.Item label="emoji">{detailRecord.emoji || '—'}</Descriptions.Item>
            <Descriptions.Item label="稀有度">{renderLevel(detailRecord.level)}</Descriptions.Item>
            <Descriptions.Item label="标签">{detailRecord.labelZh || '—'}</Descriptions.Item>
            <Descriptions.Item label="类别">{detailRecord.category || '—'}</Descriptions.Item>
            <Descriptions.Item label="气候分类">{detailRecord.climateCategory || '—'}</Descriptions.Item>
            <Descriptions.Item label="生命指数">{fmtNum(detailRecord.lifeIndex)}</Descriptions.Item>
            <Descriptions.Item label="日产量下限">{fmtNum(detailRecord.dailyProductionMin)}</Descriptions.Item>
            <Descriptions.Item label="日产量上限">{fmtNum(detailRecord.dailyProductionMax)}</Descriptions.Item>
            <Descriptions.Item label="实例数">{detailRecord.instanceCount ?? 0}</Descriptions.Item>
            <Descriptions.Item label="图片" span={2}>{detailRecord.texture || '—'}</Descriptions.Item>
            <Descriptions.Item label="描述" span={2}>
              <div style={{ whiteSpace: 'pre-wrap' }}>{detailRecord.description || '—'}</div>
            </Descriptions.Item>
            <Descriptions.Item label="effects" span={2}>{renderJson(detailRecord.effects)}</Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </Card>
  );
};

export default PlantTab;
